// Social links on a profile. To add a platform, add an entry to SOCIALS (and an icon in icons.js).
//
// Storage in users/<username>:
//   website   -> `link` (the field iOS already uses for the profile link)
//   others    -> socials/<id> = canonical https URL
// Anything read back is re-validated with `parse`, so a hand-edited database value can never render
// as a non-http(s) link.

// Mirrors the iOS String.validateUrl(): accepts "example.com" or "https://example.com".
// Returns an http(s) URL string, or null if the value isn't a usable link.
export const normalizeUrl = (value) => {
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(withScheme);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname ? url.href : null;
  } catch (e) {
    return null;
  }
};

const onDomain = (href, ...domains) => {
  const host = new URL(href).hostname.toLowerCase();
  return domains.some((d) => host === d || host.endsWith(`.${d}`));
};

const HANDLE = /^[A-Za-z0-9._]{1,30}$/;

const instagramHandle = (value) => {
  const bare = value.trim().replace(/^@/, '');
  if (HANDLE.test(bare)) return bare;
  const href = normalizeUrl(value.trim());
  if (!href || !onDomain(href, 'instagram.com')) return null;
  const first = new URL(href).pathname.split('/').filter(Boolean)[0];
  return first && HANDLE.test(first) ? first : null;
};

export const SOCIALS = [
  {
    id: 'instagram',
    label: 'Instagram',
    placeholder: '@username',
    error: 'Enter an Instagram username or profile link',
    parse: (value) => {
      const handle = instagramHandle(value);
      return handle ? `https://instagram.com/${handle}` : null;
    },
    // What the edit field shows for a stored value.
    toInput: (url) => {
      const handle = url && instagramHandle(url);
      return handle ? `@${handle}` : url || '';
    },
  },
  {
    id: 'spotify',
    label: 'Spotify',
    placeholder: 'open.spotify.com/artist/…',
    error: 'Enter a Spotify link (e.g. open.spotify.com/artist/…)',
    parse: (value) => {
      const href = normalizeUrl(value.trim());
      return href && onDomain(href, 'spotify.com', 'spotify.link') ? href : null;
    },
    toInput: (url) => url || '',
  },
  {
    id: 'website',
    label: 'Website',
    field: 'link',
    placeholder: 'example.com',
    error: 'Enter a valid URL (e.g. example.com or https://example.com)',
    parse: (value) => normalizeUrl(value.trim()),
    toInput: (url) => url || '',
  },
];

// The stored value for each platform, from a users/<username> record.
export const readSocials = (user = {}) => {
  const out = {};
  SOCIALS.forEach((s) => {
    out[s.id] = (s.field ? user[s.field] : user.socials && user.socials[s.id]) || '';
  });
  return out;
};

// Links worth showing for a profile: only values that still pass validation.
export const socialLinks = (stored) =>
  SOCIALS.map((s) => ({ ...s, url: stored[s.id] ? s.parse(String(stored[s.id])) : null })).filter((s) => s.url);
